var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { DominoBuilderError, DominoBuilderErrorScope, DominoBuilderErrorSeverity, DominoBuilderErrorType, } from '../errors';
import { getIsAnyType } from '../helpers';
import { PrimitiveTypes } from '../types';
export const constructContextSymbol = (contextProperties, resourceRegistry, symbolTable) => __awaiter(void 0, void 0, void 0, function* () {
    const contextTypeDefinition = {
        name: 'Context',
        properties: contextProperties,
        spec: {},
    };
    const flowSymbol = {
        id: 'Context',
        actionHandle: undefined,
        primogenitor: 'Context',
        lineNumber: -1,
        label: 'Trigger Context',
        type: 'Context',
    };
    const proxy = createDynamicSymbolProxy(flowSymbol, { type: contextTypeDefinition }, resourceRegistry);
    symbolTable.setScopedSymbol('Context', proxy, 'global');
    yield constructTypeIndex(proxy, symbolTable, resourceRegistry, 'global');
    return proxy;
});
export const constructFlowSymbols = (name, typeDefinition, resourceRegistry, symbolTable, action) => __awaiter(void 0, void 0, void 0, function* () {
    if (!name) {
        return undefined; // should probably throw here so that error can be added to action
    }
    const { handle, scopeRef, lineNumber } = action;
    let flowSymbol = {
        id: name,
        actionHandle: handle,
        primogenitor: name,
        lineNumber,
        label: name,
        type: typeDefinition.name,
    };
    const proxy = createDynamicSymbolProxy(flowSymbol, { type: typeDefinition }, resourceRegistry);
    symbolTable.setScopedSymbol(name, proxy, scopeRef);
    // walk the proxy's property tree, create type index entries, and load required entities into cache
    yield constructTypeIndex(proxy, symbolTable, resourceRegistry, scopeRef);
    return proxy;
});
export function constructTypeIndex(
/**
 * Must be a proxied FlowSymbol
 */
flowSymbol, symbolTable, resourceRegistry, scopeRef) {
    return __awaiter(this, void 0, void 0, function* () {
        let deproxiedSym = flowSymbol.__deproxy();
        if (isPrimitiveType(deproxiedSym.type)) {
            symbolTable.addToSymbolSearchIndex(deproxiedSym, scopeRef);
            return;
        }
        yield Promise.all(Object.keys(flowSymbol).map((k) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            let prop = flowSymbol[k];
            let deproxiedProp = (_a = prop.__deproxy()) !== null && _a !== void 0 ? _a : prop;
            // if this condition is true, the getter of flowSymbol[k] MAY have returned an incomplete proxy
            // due to the entity definition not being loaded into cache, so we load it into cache and re-get prop
            if (!isPrimitiveType(deproxiedProp.type) && deproxiedProp.type !== 'any') {
                try {
                    yield resourceRegistry.getResource(deproxiedProp.type);
                }
                catch (error) { }
                prop = flowSymbol[k];
                deproxiedProp = (_b = prop.__deproxy()) !== null && _b !== void 0 ? _b : prop;
            }
            // add the deproxied symbol to cache
            symbolTable.addToSymbolSearchIndex(deproxiedProp, scopeRef);
            // if type is non-primitive, continue walking the property tree
            if (!isPrimitiveType(deproxiedProp.type)) {
                constructTypeIndex(prop, symbolTable, resourceRegistry, scopeRef);
            }
        })));
    });
}
export const isPrimitiveType = (type) => {
    return !!Object.values(PrimitiveTypes).find(t => t === type);
};
export const getQualifiedSymbolId = (s) => {
    let id = s.id;
    let c = s.parent;
    while (c) {
        id = c.id + '.' + id;
        c = c.parent;
    }
    return id;
};
/**
 * Creates a dynamic proxy chain for a result. A proxy obfuscates the FlowSymbol metadata properties, replacing them with the properties of entityDefinition. Deproxying
 * the proxy restores the original FlowSymbol, along with its metadata properties.
 *
 * If no entity definition is provided to a proxy or a requested property does not exist in the entity definition, the proxy will respond with another proxy containing
 * an error. This allows expression eval to continue evaluating a faulty expression property chain without throwing.
 *
 * Otherwise, a valid FlowSymbol proxy, with associated metadata, will be returned.
 *
 * @param {FlowSymbol} flowSymbol - the symbol to be proxied
 * @param {string} entityDefinition - the entity shape of the symbol being proxied
 * @param {MetadataRegistryClient} metadataRegistryClient
 * @returns {Proxy<FlowSymbol>}
 */
export function createDynamicSymbolProxy(flowSymbol, typeDefinition, resourceRegistry) {
    const symbolProxy = new Proxy(flowSymbol, {
        get: (target, symbol, _receiver) => {
            var _a, _b;
            if (symbol === '__deproxy') {
                return () => target;
            }
            else {
                let property = (_b = (_a = typeDefinition === null || typeDefinition === void 0 ? void 0 : typeDefinition.type) === null || _a === void 0 ? void 0 : _a.properties) === null || _b === void 0 ? void 0 : _b.find(p => p.name === symbol);
                if (!typeDefinition || !property) {
                    return createDynamicSymbolProxy({
                        id: symbol,
                        actionHandle: flowSymbol.actionHandle,
                        type: undefined,
                        primogenitor: symbol,
                        lineNumber: flowSymbol.lineNumber,
                        parent: flowSymbol,
                        errors: [
                            new DominoBuilderError(DominoBuilderErrorType.InvalidSymbolReference, DominoBuilderErrorScope.DESIGN, DominoBuilderErrorSeverity.ERROR, `'${symbol}' does not exist on type '${flowSymbol.type}'`, {
                                path: `Symbol::${symbol}`,
                            }),
                        ],
                        __deproxy: () => undefined,
                    });
                }
                return createDynamicSymbolProxy({
                    id: property.name,
                    actionHandle: flowSymbol.actionHandle,
                    type: getIsAnyType(property.type) ? property.type : '',
                    label: property.label,
                    description: property.description,
                    primogenitor: flowSymbol.primogenitor,
                    lineNumber: flowSymbol.lineNumber,
                    parent: flowSymbol,
                    __deproxy: () => undefined,
                }, {
                    type: resourceRegistry.getResourceFromCache(getIsAnyType(property.type) ? property.type : ''),
                }, resourceRegistry);
            }
        },
        ownKeys: _ => {
            var _a, _b, _c;
            const uniqueKeys = new Set((_c = (_b = (_a = typeDefinition === null || typeDefinition === void 0 ? void 0 : typeDefinition.type) === null || _a === void 0 ? void 0 : _a.properties) === null || _b === void 0 ? void 0 : _b.filter(e => e).map(p => p.name)) !== null && _c !== void 0 ? _c : []);
            return [...uniqueKeys];
        },
        getOwnPropertyDescriptor(_target, _prop) {
            return { enumerable: true, configurable: true };
        },
        set(target, prop, value, _receiver) {
            if (prop === 'lineNumber') {
                target.lineNumber = value;
            }
            return true;
        },
    });
    return symbolProxy;
}
//# sourceMappingURL=symbols.js.map